import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConnectedApp } from '@couch/shared';
import { api, errorMessage } from '../../api/client';

export const CONNECTED_APPS_KEY = ['connected-apps'] as const;
const KEY = CONNECTED_APPS_KEY;
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'never');

function AppRow({ app }: { app: ConnectedApp }) {
  const queryClient = useQueryClient();
  const disconnect = useMutation({ mutationFn: () => api.disconnectApp(app.id), onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }) });
  return (
    <tr>
      <td>{app.name}</td>
      <td>{when(app.createdAt)}</td>
      <td>{when(app.lastUsedAt)}</td>
      <td><button type="button" className="button-quiet" aria-label={`Disconnect ${app.name}`} disabled={disconnect.isPending} onClick={() => { disconnect.mutate(); }}>Disconnect</button></td>
    </tr>
  );
}

/** Apps connected through OAuth — the Claude apps' "custom connector" (doc 37). */
export function ConnectedAppsPanel() {
  const apps = useQuery({ queryKey: KEY, queryFn: api.connectedApps });
  const url = `${window.location.origin}/api/v1/mcp`;
  const list = apps.data?.apps ?? [];
  return (
    <section className="panel" aria-labelledby="connected-apps-title">
      <h2 id="connected-apps-title">Connected apps</h2>
      <p className="muted">
        Add Couch to Claude (desktop, web or phone): Settings → Connectors → Add custom connector, URL <code>{url}</code>.
        Claude then asks you here to allow it — no token to copy.
      </p>
      {apps.isError && <p role="alert" className="form-error">{errorMessage(apps.error)}</p>}
      {apps.isSuccess && list.length === 0 && <p className="muted">No app is connected.</p>}
      {list.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead><tr><th scope="col">App</th><th scope="col">Connected</th><th scope="col">Last used</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
            <tbody>{list.map((a) => <AppRow key={a.id} app={a} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
